/**
 * TaskPriorityPanel - DR Queue Task Priority List
 *
 * Single-list view with a GP / FACEM toggle (default: FACEM).
 * Shows In Progress tasks on top, then Up Next (pending, sorted by priority).
 * Tasks are rendered as compact grid cards.
 */

import { useMemo, useState } from 'react';
import {
  Card,
  Tag,
  Typography,
  Space,
  Empty,
  Modal,
  InputNumber,
  Divider,
  Segmented,
} from 'antd';
import {
  ArrowUpOutlined,
  EditOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  VideoCameraOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useUnifiedDashboardStore } from '../../store';
import { updateTaskPriority } from '../../utils/fetchUnifiedData';
import usePatientNameMap from '../../hooks/usePatientNameMap';

const { Text } = Typography;

const ACTIVE_STATUSES = ['pending', 'reserved', 'assigned'];

const STATUS_COLORS = {
  pending: 'orange',
  reserved: 'blue',
  assigned: 'green',
};

const STATUS_LABELS = {
  pending: 'In Queue',
  reserved: 'Reserved',
  assigned: 'With Agent',
};

const TRIAGE_COLORS = {
  CAT1: '#ff4d4f',
  CAT2: '#fa8c16',
  CAT3: '#fadb14',
  CAT4: '#52c41a',
  CAT5: '#1890ff',
};

const getPriorityColor = (priority) => {
  if (priority >= 100) return '#ff4d4f';
  if (priority >= 50) return '#fa8c16';
  if (priority >= 10) return '#fadb14';
  if (priority >= 1) return '#1890ff';
  return '#d9d9d9';
};

const formatAge = (ageSeconds) => {
  if (!ageSeconds) return null;
  const minutes = Math.floor(ageSeconds / 60);
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h${remainingMinutes > 0 ? ` ${remainingMinutes}m` : ''}`;
};

const isGpQueue = (queueName) => queueName?.startsWith('DR_GP');

const calculateQueuePosition = (newPriority, taskSid, allTasks, queueName) => {
  const isGp = isGpQueue(queueName);
  const pendingInQueue = allTasks.filter(
    (t) =>
      t.taskQueueFriendlyName?.startsWith('DR_') &&
      isGpQueue(t.taskQueueFriendlyName) === isGp &&
      t.assignmentStatus === 'pending' &&
      t.sid !== taskSid
  );
  const sorted = [...pendingInQueue].sort(
    (a, b) => b.priority - a.priority || b.age - a.age
  );
  let position = 1;
  for (const t of sorted) {
    if (newPriority > t.priority) break;
    if (newPriority === t.priority) break;
    position++;
  }
  return { position, total: sorted.length + 1 };
};

// ── TaskCard ──────────────────────────────────────────────────

const TaskCard = ({ task, isPending, onEditPriority, patientNameMap }) => {
  const attrs = task.attributes || {};
  const patientName =
    patientNameMap?.get(task.sid)?.join(', ') || attrs.name || null;
  const triageCat = attrs.triageCat || null;
  const siteCode = attrs.videoSiteCode || null;
  const priorityColor = getPriorityColor(task.priority);
  const isVideo = task.taskChannelUniqueName === 'video';

  const tagStyle = { margin: 0, fontSize: 13, padding: '2px 8px' };

  return (
    <Card
      size="small"
      style={{
        borderLeft: `4px solid ${isPending ? priorityColor : '#52c41a'}`,
        opacity: isPending ? 1 : 0.85,
      }}
      styles={{ body: { padding: '14px 16px' } }}
    >
      {/* Header: name + priority */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <Space size={6}>
          {isVideo ? (
            <VideoCameraOutlined style={{ fontSize: 15, color: '#595959' }} />
          ) : (
            <PhoneOutlined style={{ fontSize: 15, color: '#595959' }} />
          )}
          <Text strong style={{ fontSize: 16 }}>
            {patientName || 'Unknown'}
          </Text>
        </Space>

        {isPending ? (
          <Tag
            color={priorityColor}
            style={{
              ...tagStyle,
              fontWeight: 600,
              cursor: 'pointer',
            }}
            onClick={() => onEditPriority(task)}
          >
            Priority: {task.priority} <EditOutlined />
          </Tag>
        ) : (
          <Tag color={priorityColor} style={{ ...tagStyle, fontWeight: 600 }}>
            Priority: {task.priority}
          </Tag>
        )}
      </div>

      {/* Details: one attribute per row */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <div>
          <Tag color={STATUS_COLORS[task.assignmentStatus]} style={tagStyle}>
            Status:{' '}
            {STATUS_LABELS[task.assignmentStatus] || task.assignmentStatus}
          </Tag>
        </div>

        <div>
          <Tag style={tagStyle}>Queue: {task.taskQueueFriendlyName}</Tag>
        </div>

        {triageCat && (
          <div>
            <Tag
              style={{
                ...tagStyle,
                backgroundColor: TRIAGE_COLORS[triageCat] || '#f0f0f0',
                color: triageCat === 'CAT3' ? '#000' : '#fff',
                fontWeight: 600,
              }}
            >
              Triage: {triageCat}
            </Tag>
          </div>
        )}

        {siteCode && (
          <div>
            <Tag icon={<EnvironmentOutlined />} style={tagStyle}>
              Site: {siteCode}
            </Tag>
          </div>
        )}

        {task.age && (
          <div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              <ClockCircleOutlined /> {formatAge(task.age)}
            </Text>
          </div>
        )}
      </div>
    </Card>
  );
};

// ── Main Panel ───────────────────────────────────────────────

const TaskPriorityPanel = () => {
  const { rawTaskRouter, rawActiveCalls, fetchAndResetTimer } =
    useUnifiedDashboardStore();

  const [mode, setMode] = useState('facem');
  const [inProgressCollapsed, setInProgressCollapsed] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newPriority, setNewPriority] = useState(0);
  const [updating, setUpdating] = useState(false);

  const patientNameMap = usePatientNameMap(rawActiveCalls);

  const allDrTasks = useMemo(() => {
    const tasks = rawTaskRouter?.tasks || [];
    return tasks.filter(
      (task) =>
        task.taskQueueFriendlyName?.startsWith('DR_') &&
        ACTIVE_STATUSES.includes(task.assignmentStatus)
    );
  }, [rawTaskRouter]);

  const activeTasks = useMemo(() => {
    if (mode === 'gp') {
      return allDrTasks.filter((t) => isGpQueue(t.taskQueueFriendlyName));
    }
    return allDrTasks.filter((t) => !isGpQueue(t.taskQueueFriendlyName));
  }, [allDrTasks, mode]);

  const inProgress = useMemo(
    () =>
      activeTasks
        .filter((t) => t.assignmentStatus !== 'pending')
        .sort((a, b) => b.age - a.age),
    [activeTasks]
  );

  const pending = useMemo(
    () =>
      activeTasks
        .filter((t) => t.assignmentStatus === 'pending')
        .sort((a, b) => b.priority - a.priority || b.age - a.age),
    [activeTasks]
  );

  const queuePositionPreview = useMemo(() => {
    if (!selectedTask || newPriority === null || newPriority === undefined)
      return null;
    return calculateQueuePosition(
      newPriority,
      selectedTask.sid,
      rawTaskRouter?.tasks || [],
      selectedTask.taskQueueFriendlyName
    );
  }, [selectedTask, newPriority, rawTaskRouter]);

  const handleEditPriority = (task) => {
    setSelectedTask(task);
    setNewPriority(task.priority);
    setModalOpen(true);
  };

  const handleConfirm = async () => {
    if (!selectedTask) return;
    setUpdating(true);
    try {
      await updateTaskPriority(selectedTask.sid, newPriority);
      console.log(
        `[TaskPriority] Updated task ${selectedTask.sid} priority: ${selectedTask.priority} → ${newPriority}`
      );
      setModalOpen(false);
      setSelectedTask(null);
      fetchAndResetTimer().catch(console.error);
    } catch (err) {
      Modal.error({
        title: 'Failed to update priority',
        content:
          err.response?.data?.error || err.message || 'Please try again.',
      });
    } finally {
      setUpdating(false);
    }
  };

  const gpCount = useMemo(
    () => allDrTasks.filter((t) => isGpQueue(t.taskQueueFriendlyName)).length,
    [allDrTasks]
  );
  const facemCount = useMemo(
    () => allDrTasks.filter((t) => !isGpQueue(t.taskQueueFriendlyName)).length,
    [allDrTasks]
  );

  return (
    <>
      <div
        style={{
          padding: '12px 16px',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Toggle */}
        <div style={{ marginBottom: 14 }}>
          <Segmented
            value={mode}
            onChange={setMode}
            options={[
              { label: `FACEM (${facemCount})`, value: 'facem' },
              { label: `GP (${gpCount})`, value: 'gp' },
            ]}
            block
            style={{ fontSize: 14 }}
          />
        </div>

        {/* Task list */}
        {activeTasks.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`No active ${mode === 'gp' ? 'GP' : 'FACEM'} tasks`}
            style={{ padding: '20px 0' }}
          />
        ) : (
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {inProgress.length > 0 && (
              <>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setInProgressCollapsed((v) => !v)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setInProgressCollapsed((v) => !v);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 8px',
                    cursor: 'pointer',
                    borderRadius: 6,
                    background: '#f5f5f5',
                    userSelect: 'none',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = '#e8e8e8')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = '#f5f5f5')
                  }
                >
                  {inProgressCollapsed ? (
                    <RightOutlined style={{ fontSize: 11 }} />
                  ) : (
                    <DownOutlined style={{ fontSize: 11 }} />
                  )}
                  <Text strong style={{ fontSize: 13 }}>
                    IN PROGRESS ({inProgress.length})
                  </Text>
                </div>
                {!inProgressCollapsed && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    {inProgress.map((task) => (
                      <TaskCard
                        key={task.sid}
                        task={task}
                        isPending={false}
                        patientNameMap={patientNameMap}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {pending.length > 0 && (
              <>
                {inProgress.length > 0 && (
                  <Divider style={{ margin: '8px 0' }} />
                )}
                <Text strong style={{ fontSize: 13, padding: '0 4px' }}>
                  <ArrowUpOutlined /> UP NEXT ({pending.length})
                </Text>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  {pending.map((task) => (
                    <TaskCard
                      key={task.sid}
                      task={task}
                      isPending
                      onEditPriority={handleEditPriority}
                      patientNameMap={patientNameMap}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Priority Edit Modal */}
      <Modal
        title="Change Task Priority"
        open={modalOpen}
        onOk={handleConfirm}
        onCancel={() => {
          setModalOpen(false);
          setSelectedTask(null);
        }}
        okText="Confirm"
        cancelText="Cancel"
        confirmLoading={updating}
        okButtonProps={{
          disabled:
            newPriority === selectedTask?.priority ||
            newPriority === null ||
            newPriority === undefined,
        }}
      >
        {selectedTask && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div
              style={{
                background: '#fafafa',
                borderRadius: 6,
                padding: 12,
              }}
            >
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Text>
                  <Text strong>Patient: </Text>
                  {patientNameMap?.get(selectedTask.sid)?.join(', ') ||
                    selectedTask.attributes?.name ||
                    'Unknown'}
                </Text>
                {selectedTask.attributes?.videoSiteCode && (
                  <Text>
                    <Text strong>Site: </Text>
                    {selectedTask.attributes.videoSiteCode}
                  </Text>
                )}
                <Text>
                  <Text strong>Queue: </Text>
                  {selectedTask.taskQueueFriendlyName}
                </Text>
                {selectedTask.attributes?.triageCat && (
                  <Text>
                    <Text strong>Triage: </Text>
                    {selectedTask.attributes.triageCat}
                  </Text>
                )}
              </Space>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Text strong>Current Priority:</Text>
              <Tag
                color={getPriorityColor(selectedTask.priority)}
                style={{ fontWeight: 600 }}
              >
                {selectedTask.priority}
              </Tag>
              <Text strong>→</Text>
              <Text strong>New:</Text>
              <InputNumber
                min={0}
                max={1000}
                value={newPriority}
                onChange={(val) => setNewPriority(val)}
                style={{ width: 100 }}
              />
            </div>

            {queuePositionPreview && newPriority !== selectedTask.priority && (
              <div
                style={{
                  background: '#e6f7ff',
                  border: '1px solid #91d5ff',
                  borderRadius: 6,
                  padding: 12,
                }}
              >
                <Text>
                  After this change, this task will be{' '}
                  <Text strong>
                    #{queuePositionPreview.position} of{' '}
                    {queuePositionPreview.total}
                  </Text>{' '}
                  pending tasks across{' '}
                  <Text strong>
                    all{' '}
                    {isGpQueue(selectedTask.taskQueueFriendlyName)
                      ? 'GP'
                      : 'FACEM'}{' '}
                    queues
                  </Text>
                  .
                </Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default TaskPriorityPanel;
